import {
  collection,
  serverTimestamp,
  type CollectionReference,
  type DocumentData,
  type Firestore,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
  type DocumentReference,
} from 'firebase/firestore'
import type { StreamInterface } from 'tricklejs/dist/types'
import { ZodError } from 'zod'
import { modelListStream } from '../../firestore-mobx/stream'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import type { FirestoreClickWrapAccepted } from './schema'
import { schema } from './schema'
import { addDocWithError } from '../../firestore-mobx/fetch'
import { ClickWrapAccepted } from '../../models/ClickWrapAccepted'

export * from './schema'

const converter: FirestoreDataConverter<FirestoreClickWrapAccepted> = {
  toFirestore: (data) => data,
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    try {
      return schema.parse(data)
    } catch (e) {
      if (e instanceof ZodError) {
        console.error('Error parsing click wrap accepted', e.errors)
      }
      throw e
    }
  },
}

const getColRef = (
  firestore: Firestore,
  userId: string
): CollectionReference<FirestoreClickWrapAccepted, DocumentData> => {
  return collection(
    firestore,
    'user_profile',
    userId,
    'click_wrap_accepted'
  ).withConverter(converter)
}

export const acceptClickWraps = (
  repository: FirebaseRepository,
  params: { clickWrapIds: string[] }
): Promise<
  Array<DocumentReference<FirestoreClickWrapAccepted, DocumentData>>
> => {
  const uid = repository.uid
  const ref = getColRef(repository.firestore, uid)

  const acceptedClickWraps = Promise.all(
    params.clickWrapIds.map(async (clickWrapId) => {
      return await addDocWithError(ref, {
        acceptedAt: serverTimestamp(),
        clickWrapId: clickWrapId,
      })
    })
  )
  return acceptedClickWraps
}

export const getClickWrapAcceptedList = (
  repository: FirebaseRepository,
  params: { userId: string }
): StreamInterface<ClickWrapAccepted[]> => {
  const ref = getColRef(repository.firestore, params.userId)
  return modelListStream(repository, ref, ClickWrapAccepted)
}
